/**
 * useAdminActiveCauldron — reactive resolver for the active admin cauldron.
 *
 * Modelled on the customer app's `useActiveCauldron` but bound to the admin
 * registry (`src/admin/config/cauldrons.js`) and the admin router.
 *
 * Resolution priority:
 *   1. `route.meta.cauldron` — explicit tag from the admin router.
 *   2. `routePrefixes` match — fallback for any untagged route.
 *   3. `null` — unrecognised route; the home dashboard is outside the workspaces.
 */
import {
  ADMIN_CAULDRONS,
  getAdminCauldronById,
  getAdminCauldronByPath,
} from '@/admin/config/cauldrons';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

export function useAdminActiveCauldron() {
  // `useRoute()` is undefined in component tests without a router; the empty
  // fallback keeps the composable mountable there.
  const route = useRoute() || {};

  const activeCauldron = computed(() => {
    if (route.path === '/') return null;
    const meta = route.meta || {};
    if (meta.cauldron) {
      const fromMeta = getAdminCauldronById(meta.cauldron);
      if (fromMeta) return fromMeta;
    }
    return getAdminCauldronByPath(route.path);
  });

  const activeCauldronId = computed(() => activeCauldron.value?.id ?? null);

  function isActive(id) {
    return activeCauldronId.value === id;
  }

  return {
    cauldrons: ADMIN_CAULDRONS,
    activeCauldron,
    activeCauldronId,
    isActive,
  };
}
